import { useMergeRefs } from '@libs/react/ref';
import styles from './RangeInput.module.scss';
import {
  ChangeEvent,
  CSSProperties,
  forwardRef,
  ReactNode,
  Ref,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { InputShared } from './InputShared';

interface Props {
  children?: ReactNode;
  defaultValue?: number;
  disabled?: boolean;
  id?: string;
  label: string;
  max?: number;
  min?: number;
  name?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onValue?: (value: number) => void;
  progress?: boolean;
  progressStyle?: CSSProperties;
  readOnly?: boolean;
  required?: boolean;
  step?: number | 'any';
  value?: number;
  width?: string;
}

function Implemenation(
  {
    children,
    defaultValue,
    disabled,
    id: idProps,
    label,
    max = 100,
    min = 0,
    name,
    onChange,
    onValue,
    progress,
    progressStyle,
    readOnly,
    required,
    step = 1,
    value,
  }: Props,
  ref: Ref<HTMLInputElement>,
) {
  const id = useId();
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = useMergeRefs(internalRef, ref);
  const [currentValue, setCurrentValue] = useState(
    value ?? defaultValue ?? (min + max) / 2,
  );

  useEffect(() => {
    if (value !== undefined) {
      setCurrentValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (defaultValue) {
      setCurrentValue(defaultValue);
    }
  }, [defaultValue]);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(Number(event.target.value));
    onChange?.(event);
  };

  const commitValue = () => {
    const snapStep = step === 'any' ? 1 : step;
    const stepped =
      Math.round((currentValue - min) / snapStep) * snapStep + min;
    const snapped = Math.min(max, Math.max(min, stepped));
    setCurrentValue(snapped);
    if (value === undefined && internalRef.current) {
      internalRef.current.value = String(snapped);
    }
    onValue?.(snapped);
  };

  const percent = ((currentValue - min) / (max - min)) * 100;

  return (
    <InputShared id={id} label={label}>
      <div className={styles['range-input']}>
        <div className={styles['range-input-control']}>
          <div className={styles['range-input-track']} />
          {progress && (
            <div
              className={styles['range-input-progress']}
              style={{
                width: `${percent}%`,
                ...progressStyle,
                // Any background-image (e.g. a gradient) in progressStyle is
                // authored against the full track, but this div is only
                // `percent`-wide — without this, the image would rescale to
                // fit that shrunken box and always show its end color at the
                // visible edge. Sizing the background up by the inverse of
                // percent keeps it fixed to the full track, so this div acts
                // as a window revealing the correct slice of it.
                backgroundSize:
                  percent > 0 ? `${(100 / percent) * 100}% 100%` : '100% 100%',
                backgroundPosition: 'left',
              }}
            />
          )}
          {!readOnly && (
            <div
              className={styles['range-input-thumb']}
              style={{ left: `${percent}%` }}
            />
          )}
          <input
            ref={inputRef}
            className={styles['range-input-element']}
            defaultValue={defaultValue}
            disabled={disabled || readOnly}
            id={idProps ?? id}
            max={max}
            min={min}
            name={name}
            onChange={onInputChange}
            onKeyUp={commitValue}
            onPointerUp={commitValue}
            readOnly={readOnly}
            required={required}
            step={step}
            type="range"
            value={value}
          />
        </div>
        {children && (
          <div className={styles['range-input-marks']}>{children}</div>
        )}
      </div>
    </InputShared>
  );
}

export const RangeInput = forwardRef<HTMLInputElement, Props>(Implemenation);
